/**
 * <cart-drawer> — Cart Drawer Web Component (entry module)
 *
 * Synchronous, server-truth cart. The drawer never displays a price, total,
 * quantity, badge count, or line item the server hasn't rendered. Every
 * mutation — user-initiated or programmatic (Rebuy, upsells) —
 * follows one pipeline, serialized by a promise-chain mutex:
 *
 *   flush pending stepper intake → lock the whole cart → mark the loading
 *   target (@theme/cart/loading: skeleton placeholder / row shimmer)
 *   → one request → full replacement render of the returned cart-drawer
 *   section → unlock
 *
 * There is no optimistic state, no client-side price math, and no DOM
 * patching of cart data. The only client-injected DOM is the loading
 * skeleton/shimmer, which carries zero server data and is wiped by the
 * render. On failure the drawer resyncs from the server and shows a
 * dismissible banner, so every mutation attempt — success or failure —
 * ends with a server render.
 *
 * Quantity steppers are the one concession to feel: clicks accumulate on the
 * stepper digit for 300ms (input echo, not cart state), then fire one request
 * with the final quantity. Any other mutation flushes pending intake first so
 * user input is never silently discarded.
 *
 * All interaction handling is delegated on the <cart-drawer> host element so
 * wholesale innerHTML replacement cannot orphan listeners.
 *
 * Free gifts (2ez-gift app): each mutation plans the implied gift
 * adds/removes against the projected cart (@theme/cart/gift) and folds them
 * into its own request, so gift and trigger land in one pipeline pass. What
 * can't be folded is left to the app's reactive reconciler, whose
 * `2ezgift:reconciled` event routes back through requestRefresh(). The gift
 * progress bar reads the same app's rule config (@theme/cart/gift-rules) and
 * is display-only — it never plans a mutation.
 */

import { postToCart, getSectionParam } from "@theme/cart/api";
import { planGifts, projectItems, toGiftAddItems } from "@theme/cart/gift";
/* Side-effect import: defines <gift-progress>, which the drawer's own Liquid
   renders. It re-initialises itself on every full-replacement render, so the
   render path needs no hook for it. */
import "@theme/cart/gift-bar";
import { applyLoadingState } from "@theme/cart/loading";
/* Side-effect import: defines <cart-reserve-timer>, the footer's reservation
   countdown. Same deal as <gift-progress> — it re-initialises itself on every
   full-replacement render, so the render path needs no hook for it. */
import "@theme/cart/reserve-timer";
import {
  createGiftObserver,
  observeGiftItems,
  readCartJson,
  renderSection,
} from "@theme/cart/render";
import { loadUpsell } from "@theme/cart/upsell";
import {
  closePicker,
  handlePickerClick,
  prefetchProduct,
  togglePicker,
} from "@theme/cart/variant-picker";

/* Stepper intake window — rapid +/− clicks accumulate on the stepper digit
   for this long, then one request fires with the final quantity. */
const INTAKE_MS = 300;

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._ac = new AbortController();

    /* Last server-rendered cart JSON (parsed from the section's embedded
       [data-cart-json] block). Read-only mirror — used for upsell seeding,
       line-item property lookups, and the cart:updated event detail. */
    this._cart = null;

    /* Promise-chain mutex — exactly one cart mutation in flight, FIFO. */
    this._mutex = Promise.resolve();

    /* Count of queued/in-flight ops; the whole cart locks while > 0. */
    this._pendingOps = 0;

    /* Pending stepper intake per line key: { qty, initialQty, timer }. */
    this._intake = new Map();

    /* True while a programmatic refresh is already queued (dedupe). */
    this._refreshQueued = false;

    this._ownEvents = new WeakSet();
    this._focusTrapBound = this._trapFocus.bind(this);
    this._focusableEls = null;
    this._openTrigger = null;
  }

  /* ================================================================
     LIFECYCLE
     ================================================================ */

  connectedCallback() {
    const signal = this._ac.signal;

    this._cacheDom();

    /* ── Escape key ── */
    document.addEventListener(
      "keydown",
      (e) => {
        if (
          e.key === "Escape" &&
          this.getAttribute("aria-hidden") === "false"
        ) {
          this.close();
        }
      },
      { signal },
    );

    /* ── Open triggers (header bubble lives outside the drawer) ── */
    document.querySelectorAll("[data-cart-open]").forEach((el) => {
      el.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          this._openTrigger = e.currentTarget;
          this.open();
        },
        { signal },
      );
    });

    /* ── Intercept add-to-cart forms ── */
    document.addEventListener(
      "submit",
      (e) => {
        const form = e.target;
        if (
          form.matches('form[action*="/cart/add"]') ||
          form.getAttribute("action") === "/cart/add"
        ) {
          e.preventDefault();
          this._handleAddForm(form);
        }
      },
      { signal },
    );

    /* ── Delegated click handler (survives full re-renders) ── */
    this.addEventListener("click", (e) => this._onClick(e), { signal });

    /* ── Variant picker: prefetch product JSON on intent ── */
    const prefetchFromEvent = (e) => {
      const toggle = e.target.closest?.("[data-variant-toggle]");
      if (!toggle) return;
      const item = toggle.closest(".cart-drawer__item");
      const handle = item?.dataset.productHandle;
      if (handle) prefetchProduct(handle);
    };
    this.addEventListener("pointerover", prefetchFromEvent, { signal });
    this.addEventListener("focusin", prefetchFromEvent, { signal });

    /* ── Header state. The header floats over the scrolling body, so the
       title only earns its glass pill once content has moved under it.
       Bound in the capture phase on the host (scroll doesn't bubble) so it
       survives every full-replacement render. ── */
    this.addEventListener(
      "scroll",
      (e) => {
        if (e.target !== this._body) return;
        this._syncScrolled();
      },
      { capture: true, signal },
    );

    /* ── External cart mutations (Rebuy bundle adds, other apps). All
       routed through the same pipeline as a locked resync render — one
       visual behavior for every mutation source. ── */
    document.addEventListener(
      "cart:updated",
      (e) => {
        if (this._ownEvents.has(e)) return;
        this.requestRefresh();
      },
      { signal },
    );
    window.addEventListener("cart:refresh", () => this.requestRefresh(), {
      signal,
    });

    /* ── 2ez-gift app changed the cart outside our pipeline (reactive
       backstop for mutations we couldn't fold a gift into, page-load
       reconcile of a returning qualifying cart) — repaint from server
       truth. The app only fires this after it actually mutated the cart,
       so this can't loop. ── */
    document.addEventListener(
      "2ezgift:reconciled",
      () => this.requestRefresh(),
      { signal },
    );

    /* ── Gift unwrap reveal. Replay state is server truth: the reveal
       writes a `_gift_unwrapped_<variant>` cart attribute (via the
       _persistGiftUnwrap bridge) that Liquid reads to render the row
       already open on every later render; sessionStorage bridges the gap
       until that write lands. ── */
    this._giftObserver = createGiftObserver(this);
    observeGiftItems(this);

    /* ── Hydrate the cart mirror from the server-rendered JSON block. ── */
    this._cart = readCartJson(this);
  }

  disconnectedCallback() {
    this._ac.abort();
    if (this._giftObserver) {
      this._giftObserver.disconnect();
      this._giftObserver = null;
    }
    this._intake.forEach((rec) => clearTimeout(rec.timer));
    this._intake.clear();
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this._focusTrapBound);
  }

  /** Flag the host while the body is scrolled — styles the floating header. */
  _syncScrolled() {
    this.toggleAttribute("data-scrolled", (this._body?.scrollTop || 0) > 6);
  }

  _cacheDom() {
    this._panel = this.querySelector(".cart-drawer__panel");
    this._body = this.querySelector(".cart-drawer__body");
    this._itemsContainer = this.querySelector(".cart-drawer__items");
    this._upsellSection = this.querySelector("[data-upsell-section]");
    this._upsellTrack = this.querySelector("[data-upsell-track]");
    this._syncScrolled();
  }

  /* ================================================================
     DELEGATED CLICKS
     ================================================================ */

  _onClick(e) {
    /* Close buttons + overlay */
    if (e.target.closest("[data-drawer-close]")) {
      this.close();
      return;
    }

    /* Error banner dismiss */
    const dismiss = e.target.closest("[data-error-dismiss]");
    if (dismiss) {
      this._hideError();
      return;
    }

    /* Variant picker toggle (variant pill on a line item) */
    const variantToggle = e.target.closest("[data-variant-toggle]");
    if (variantToggle) {
      const item = variantToggle.closest(".cart-drawer__item");
      if (item) togglePicker(this, item);
      return;
    }

    /* Click inside an open picker — option selection */
    const pickerEl = e.target.closest("[data-variant-picker]");
    if (pickerEl && pickerEl.getAttribute("aria-hidden") === "false") {
      handlePickerClick(this, pickerEl, e.target);
      return;
    }

    /* Quantity steppers — accumulate into the intake window */
    const qtyBtn = e.target.closest("[data-qty-change]");
    if (qtyBtn) {
      const item = qtyBtn.closest(".cart-drawer__item");
      if (!item) return;
      this._intakeClick(item, parseInt(qtyBtn.dataset.qtyChange, 10));
      return;
    }

    /* Remove button */
    const removeBtn = e.target.closest("[data-remove]");
    if (removeBtn) {
      const item = removeBtn.closest(".cart-drawer__item");
      if (!item) return;
      /* Grouped bundle card — remove every member line atomically in one
         cart/update.js call so a partial bundle never exists server-side. */
      if (item.dataset.bundleKeys) {
        const keys = item.dataset.bundleKeys.split(",").filter(Boolean);
        if (keys.length > 0) this._removeKeys(keys);
        return;
      }
      /* Discount-split merge card — one row standing in for several line keys.
         Remove every underlying line in one atomic cart/update.js call. */
      if (item.dataset.mergeKeys) {
        const keys = item.dataset.mergeKeys.split(",").filter(Boolean);
        if (keys.length > 0) {
          this._cancelIntake(item.dataset.key);
          this._removeKeys(keys);
          return;
        }
      }
      const key = item.dataset.key;
      this._cancelIntake(key);
      this._change(key, 0);
      return;
    }

    /* Upsell quick-add */
    const upsellBtn = e.target.closest("[data-upsell-add]");
    if (upsellBtn) {
      const variantId = parseInt(upsellBtn.dataset.upsellAdd, 10);
      const price = parseInt(upsellBtn.dataset.upsellPrice, 10);
      if (variantId) {
        this.addItems(
          [
            {
              id: variantId,
              quantity: 1,
              price: Number.isFinite(price) ? price : undefined,
            },
          ],
          { openDrawer: false },
        ).catch(() => {});
      }
      return;
    }

    /* Checkout CTA — flush any pending stepper intake first so checkout
       never sees a cart missing quantity clicks the user already made,
       then navigate once the pipeline has drained. */
    const ctaBtn = e.target.closest("[data-cta]");
    if (ctaBtn) {
      const url = ctaBtn.dataset.checkoutUrl || "/checkout";
      this._flushIntake();
      if (this._pendingOps > 0) {
        this._enqueue(() => {
          window.location.href = url;
        });
        return;
      }
      window.location.href = url;
      return;
    }

    /* Fallthrough — click landed on neither a picker toggle nor inside an
       open picker, so collapse any open picker. */
    this.querySelectorAll('[data-variant-picker][aria-hidden="false"]').forEach(
      (el) => {
        const item = el.closest(".cart-drawer__item");
        if (item) closePicker(item);
      },
    );
  }

  /* ================================================================
     STEPPER INTAKE
     Clicks update only the stepper digit (input echo); after INTAKE_MS
     of quiet, one request fires with the final quantity.
     ================================================================ */

  _intakeClick(itemEl, delta) {
    if (!Number.isFinite(delta)) return;
    /* The lock's pointer-events guard doesn't cover keyboard activation —
       ignore stepper input while busy so an intake can't seed itself from a
       row that's about to be replaced. */
    if (this._pendingOps > 0) return;
    const key = itemEl.dataset.key;
    if (!key) return;

    let rec = this._intake.get(key);
    if (!rec) {
      const initial = parseInt(itemEl.dataset.quantity, 10) || 0;
      /* Discount-split merge card: the stepper drives the variant's total
         quantity across every underlying line key, not just this row. */
      const mergeKeys = itemEl.dataset.mergeKeys
        ? itemEl.dataset.mergeKeys.split(",").filter(Boolean)
        : null;
      rec = { qty: initial, initialQty: initial, timer: null, mergeKeys };
      this._intake.set(key, rec);
    }
    rec.qty = Math.max(0, rec.qty + delta);

    const valueEl = itemEl.querySelector(".cart-drawer__qty-value");
    if (valueEl) valueEl.textContent = String(rec.qty);

    clearTimeout(rec.timer);
    rec.timer = setTimeout(() => this._submitIntake(key), INTAKE_MS);
  }

  _submitIntake(key) {
    const rec = this._intake.get(key);
    if (!rec) return;
    clearTimeout(rec.timer);
    this._intake.delete(key);
    if (rec.qty === rec.initialQty) {
      /* No net change — nothing to send; the digit already matches the
         last server render. */
      return;
    }
    if (rec.mergeKeys && rec.mergeKeys.length > 1) {
      /* Merge card: a drop to zero is a full remove of every member line
         (folds gift removes); any positive total consolidates the split
         portions back into one line at the requested quantity. */
      if (rec.qty <= 0) this._removeKeys(rec.mergeKeys);
      else this._changeMerged(rec.mergeKeys, rec.qty);
      return;
    }
    this._change(key, rec.qty);
  }

  _cancelIntake(key) {
    const rec = this._intake.get(key);
    if (!rec) return;
    clearTimeout(rec.timer);
    this._intake.delete(key);
  }

  /* Flush-intake-first: submit every pending stepper quantity before any
     other mutation runs, so a full-replacement render can never silently
     discard clicks the user already made. */
  _flushIntake() {
    for (const key of [...this._intake.keys()]) {
      this._submitIntake(key);
    }
  }

  /* ================================================================
     THE PIPELINE
     ================================================================ */

  _lock() {
    this._pendingOps += 1;
    if (this._pendingOps === 1) {
      this.setAttribute("data-busy", "");
      this._panel?.setAttribute("aria-busy", "true");
    }
  }

  _unlock() {
    this._pendingOps = Math.max(0, this._pendingOps - 1);
    if (this._pendingOps === 0) {
      this.removeAttribute("data-busy");
      this._panel?.setAttribute("aria-busy", "false");
    }
  }

  _enqueue(fn) {
    const run = this._mutex.then(fn);
    this._mutex = run.then(
      () => {},
      () => {},
    );
    return run;
  }

  /**
   * Run one cart mutation through the pipeline. `requestFn(signal)` must
   * perform the request(s) and resolve with a response carrying
   * `sections["cart-drawer"]`. On failure: resync from the server, show the
   * error banner, re-throw.
   *
   * `hint` marks what this mutation touches ({ addVariantIds, shimmerKeys },
   * see @theme/cart/loading) so the affected row shimmers — or a skeleton
   * placeholder appears — while the rest of the cart sits dimmed. Applied
   * when the task starts (the DOM is the last confirmed render); the render
   * that ends the mutation wipes it, and the cleanup covers the one path
   * with no render (resync fetch failed too).
   */
  _mutate(requestFn, fallbackError, hint) {
    this._flushIntake();
    this._lock();
    return this._enqueue(async () => {
      const clearLoading = applyLoadingState(this, hint);
      try {
        const data = await requestFn(this._ac.signal);
        this._applyRender(data);
        return data;
      } catch (err) {
        if (!this._ac.signal.aborted) {
          await this._resync(err?.message || fallbackError);
        }
        throw err;
      } finally {
        clearLoading();
        this._unlock();
      }
    });
  }

  _applyRender(data) {
    const html = data?.sections?.[getSectionParam()];
    if (html) renderSection(this, html);
  }

  /**
   * Fetch the authoritative cart + section HTML and full-replace. The
   * invariant lives here: every mutation attempt ends with a server render.
   * If a message is passed, the error banner shows after the render.
   */
  async _resync(message) {
    try {
      const data = await postToCart(
        "cart/update.js",
        { sections: getSectionParam() },
        this._ac.signal,
      );
      this._applyRender(data);
    } catch {
      /* Resync failed too (network down) — keep the last confirmed render. */
    }
    if (message) this._showError(message);
  }

  /**
   * Programmatic refresh (Rebuy / external cart:updated).
   * Same lock treatment and same pipeline as user mutations; deduped so a
   * burst of events queues a single refresh.
   */
  requestRefresh() {
    if (this._refreshQueued) return;
    this._refreshQueued = true;
    this._flushIntake();
    this._lock();
    this._enqueue(async () => {
      this._refreshQueued = false;
      try {
        await this._resync();
      } finally {
        this._unlock();
      }
    });
  }

  /* ================================================================
     MUTATIONS
     ================================================================ */

  /**
   * Public API — add items to the cart. Resolves after the server has
   * confirmed the add and the drawer has re-rendered; rejects on failure.
   * The drawer opens immediately (locked, showing the last confirmed
   * render) unless opts.openDrawer === false.
   *
   * Items may carry `price` (unit, cents, pre-discount) — it never reaches
   * Shopify; it only lets the 2ez-gift projection know the post-add subtotal
   * so a newly unlocked gift can ride the same request.
   */
  addItems(items, opts = {}) {
    if (opts.openDrawer !== false) this.open();
    return this._mutate(
      (signal) => {
        const payload = items.map(({ price, ...item }) => item);
        return postToCart(
          "cart/add.js",
          {
            items: payload.concat(this._giftAddsFor(items)),
            sections: getSectionParam(),
          },
          signal,
        );
      },
      "Could not add item to cart.",
      { addVariantIds: items.map((i) => i.id) },
    );
  }

  _change(key, quantity) {
    return this._mutate(
      (signal) => {
        /* Gift removes implied by this change (dropping below a threshold)
           fold into one atomic cart/update.js call. Gift *adds* (an increase
           crossing a threshold) can't share a request with a line change —
           the app's reactive reconcile lands those a beat later and the
           2ezgift:reconciled listener repaints. */
        const giftRemoves = this._giftRemovesFor([key], quantity);
        if (giftRemoves.length > 0) {
          const updates = { [key]: quantity };
          giftRemoves.forEach((k) => {
            updates[k] = 0;
          });
          return postToCart(
            "cart/update.js",
            { updates, sections: getSectionParam() },
            signal,
          );
        }
        return postToCart(
          "cart/change.js",
          { id: key, quantity, sections: getSectionParam() },
          signal,
        );
      },
      "Could not update item.",
      { shimmerKeys: [key] },
    ).catch(() => {});
  }

  /**
   * Set the total quantity for a discount-split merge card. The card stands in
   * for several line items of one variant+properties that Shopify split apart
   * to represent a partial discount allocation. Consolidate them back into the
   * first key at the requested total and zero the rest, in one atomic
   * cart/update.js call — Shopify then re-applies the discount and the returned
   * render re-splits (or not) as appropriate. `quantity` is always > 0 here;
   * a drop to zero is routed to _removeKeys instead.
   */
  _changeMerged(keys, quantity) {
    return this._mutate(
      (signal) => {
        const [first, ...rest] = keys.map(String);
        const updates = { [first]: quantity };
        rest.forEach((k) => {
          updates[k] = 0;
        });
        /* Consolidating can only hold or drop the variant's total quantity, so
           only gift *removes* can result. Project the consolidated cart (first
           key at the new total, siblings dropped) and fold those in — mirrors
           _change so a de-qualified gift never flashes before the reconcile. */
        if (this._cart) {
          const keySet = new Set(keys.map(String));
          const projected = projectItems(this._cart)
            .map((i) => {
              if (String(i.key) !== first) return i;
              const unit = Number.isFinite(i.original_price)
                ? i.original_price
                : Math.round(i.original_line_price / (i.quantity || 1));
              return {
                ...i,
                quantity,
                original_line_price: unit * quantity,
              };
            })
            .filter((i) => {
              const k = String(i.key);
              if (keySet.has(k) && k !== first) return false;
              return i.quantity > 0;
            });
          planGifts(projected).removes.forEach((r) => {
            if (r.key && !keySet.has(String(r.key))) updates[r.key] = 0;
          });
        }
        return postToCart(
          "cart/update.js",
          { updates, sections: getSectionParam() },
          signal,
        );
      },
      "Could not update item.",
      { shimmerKeys: keys },
    ).catch(() => {});
  }

  _removeKeys(keys) {
    return this._mutate(
      (signal) => {
        const updates = {};
        keys.map(String).forEach((k) => {
          updates[k] = 0;
        });
        /* Removing lines can only ever de-qualify gifts — fold those removes
           into the same atomic update. */
        this._giftRemovesFor(Object.keys(updates), 0).forEach((k) => {
          updates[k] = 0;
        });
        return postToCart(
          "cart/update.js",
          { updates, sections: getSectionParam() },
          signal,
        );
      },
      "Could not remove bundle.",
      { shimmerKeys: keys },
    ).catch(() => {});
  }

  /**
   * Variant swap for the in-drawer picker: add the new variant first (so a
   * partial failure leaves the customer with both variants rather than
   * neither), then zero the old line. One locked pipeline task, one render
   * from the final response. Gift changes implied by the price difference
   * fold in: adds ride the add request, removes ride the zero-old update.
   * `unitPrice` is the new variant's pre-discount unit price in cents.
   */
  swapLine(oldKey, newVariantId, quantity, properties, unitPrice, removeKeys) {
    return this._mutate(
      async (signal) => {
        const item = { id: newVariantId, quantity };
        if (properties && Object.keys(properties).length > 0) {
          item.properties = properties;
        }

        /* A merge card spans several old-variant keys; every one clears so no
           unit orphans behind the new variant. `oldKey` is always among them. */
        const removedSet = new Set([String(oldKey)]);
        if (Array.isArray(removeKeys)) {
          removeKeys.forEach((k) => removedSet.add(String(k)));
        }

        let giftAdds = [];
        const updates = {};
        removedSet.forEach((k) => {
          updates[k] = 0;
        });
        if (this._cart && Number.isFinite(unitPrice)) {
          const oldItem = this._cart.items?.find(
            (i) => String(i.key) === String(oldKey),
          );
          const projected = projectItems(this._cart).filter(
            (i) => !removedSet.has(String(i.key)),
          );
          projected.push({
            key: "",
            id: newVariantId,
            product_id: oldItem?.product_id, // same product, different variant
            quantity,
            original_line_price: unitPrice * quantity,
            properties: properties || null,
          });
          const plan = planGifts(projected);
          /* Gift adds ride only mutations that GROW the cart. On a shrinking
             swap the app's discount-code snapshot may be optimistically stale
             (a code that has just stopped being applicable), and folding a
             gift in on that basis produces a line the Discount Function will
             refuse to zero — the shopper is charged for a "free" gift until
             the reactive reconciler pulls it. Removes are safe in both
             directions. The old value spans every cleared key (a merge card
             swaps out all its units at once). */
          const oldValue = (this._cart.items || [])
            .filter((i) => removedSet.has(String(i.key)))
            .reduce((sum, i) => sum + (i.original_line_price || 0), 0);
          const grows = unitPrice * quantity >= oldValue;
          giftAdds = grows ? toGiftAddItems(plan.adds) : [];
          plan.removes.forEach((r) => {
            if (r.key && !removedSet.has(String(r.key))) updates[r.key] = 0;
          });
        }

        await postToCart(
          "cart/add.js",
          { items: [item].concat(giftAdds), sections: getSectionParam() },
          signal,
        );
        return postToCart(
          "cart/update.js",
          { updates, sections: getSectionParam() },
          signal,
        );
      },
      "Could not change variant.",
      /* Departure and arrival: the old row shimmers (it's about to vanish)
         and a skeleton marks the top of the list, where the new line lands —
         unless the target variant merges into an existing line, which the
         hint resolver redirects to a shimmer on that line. */
      { shimmerKeys: [oldKey], addVariantIds: [newVariantId] },
    ).catch(() => {});
  }

  _handleAddForm(form) {
    const formData = new FormData(form);
    const id = parseInt(formData.get("id"), 10);
    if (!id) return;
    const quantity = parseInt(formData.get("quantity"), 10) || 1;
    /* Optional data-price (unit, cents) on the form feeds the gift
       projection; without it a threshold-crossing add falls back to the
       app's reactive reconcile. */
    const price = parseInt(form.dataset.price, 10);
    this.addItems([
      { id, quantity, price: Number.isFinite(price) ? price : undefined },
    ]).catch(() => {});
  }

  /* ================================================================
     2EZ-GIFT PROJECTION
     Both helpers run inside the pipeline task (mirror is current) and
     degrade to [] whenever the app is absent or a price is unknown —
     the app's reactive reconcile then covers the gap.
     ================================================================ */

  /**
   * Gift lines to fold into an add request, planned against the projected
   * post-add cart. When a new item's unit price is unknown (no `price`
   * passed and the variant isn't already in the cart) the subtotal can't
   * be projected — fold nothing. plan() removes on an add (a higher tier
   * replacing a lower one) are also left to the app: folding them would
   * need a second request, and standalone gift requests race the app.
   */
  _giftAddsFor(newItems) {
    if (!this._cart) return [];
    const projected = projectItems(this._cart);
    for (const item of newItems) {
      const quantity = item.quantity || 1;
      const existing = this._cart.items?.find((i) => i.id === item.id);
      let unit = item.price;
      if (!Number.isFinite(unit)) {
        if (!existing) return [];
        unit = existing.original_price;
      }
      projected.push({
        key: "",
        id: item.id,
        /* Known when the variant is already in the cart; a genuinely new
           variant has no knowable product id and routes any product-gated
           rule to the app's reactive path. */
        product_id: existing?.product_id,
        quantity,
        original_line_price: unit * quantity,
        properties: item.properties || null,
      });
    }
    return toGiftAddItems(planGifts(projected).adds);
  }

  /**
   * Gift line keys de-qualified by setting every key in `changedKeys` to
   * `newQuantity`. Only ever *removes* — a quantity drop can't unlock a
   * gift under any Requirement form: subtotal and product-quantity
   * requirements are both monotonic in quantity, and Code Conditions don't
   * depend on items at all.
   */
  _giftRemovesFor(changedKeys, newQuantity) {
    if (!this._cart) return [];
    const changed = new Set(changedKeys.map(String));
    const projected = projectItems(this._cart)
      .map((i) => {
        if (!changed.has(String(i.key))) return i;
        const unit = Number.isFinite(i.original_price)
          ? i.original_price
          : Math.round(i.original_line_price / (i.quantity || 1));
        return {
          ...i,
          quantity: newQuantity,
          original_line_price: unit * newQuantity,
        };
      })
      .filter((i) => i.quantity > 0);
    return planGifts(projected)
      .removes.map((r) => String(r.key))
      .filter((k) => k && !changed.has(k));
  }

  /* ================================================================
     ERROR BANNER
     A single dismissible surface at the top of the drawer. Every render
     replaces it with the (hidden) server-rendered banner, so it clears
     automatically on the next successful action.
     ================================================================ */

  _showError(message) {
    const banner = this.querySelector("[data-cart-error]");
    if (!banner) return;
    const msgEl = banner.querySelector("[data-cart-error-message]");
    if (msgEl) {
      msgEl.textContent = message || "Something went wrong. Please try again.";
    }
    banner.setAttribute("aria-hidden", "false");
  }

  _hideError() {
    const banner = this.querySelector("[data-cart-error]");
    if (banner) banner.setAttribute("aria-hidden", "true");
  }

  /* ================================================================
     OPEN / CLOSE
     ================================================================ */

  open() {
    this.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    if (this._panel) {
      this._panel.setAttribute("tabindex", "-1");
      this._panel.focus({ preventScroll: true });
    }
    this._cacheFocusable();
    document.addEventListener("keydown", this._focusTrapBound, {
      signal: this._ac.signal,
    });

    if (this._cart) loadUpsell(this, this._cart);
  }

  close() {
    this.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this._focusTrapBound);

    if (this._openTrigger) {
      this._openTrigger.focus({ preventScroll: true });
      this._openTrigger = null;
    }
  }

  _trapFocus(e) {
    if (e.key !== "Tab") return;

    const focusable = this._focusableEls;
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  _cacheFocusable() {
    if (!this._panel) return;
    this._focusableEls = this._panel.querySelectorAll(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
    );
  }

  /* ================================================================
     BRIDGES
     Helper modules call these instead of importing each other, which
     keeps the dependency graph acyclic.
     ================================================================ */

  _loadUpsell(cart) {
    return loadUpsell(this, cart);
  }

  /**
   * Persist a gift's unwrap server-side so Liquid renders the row already
   * open on every later render of this cart (cart-drawer-item.liquid checks
   * `cart.attributes._gift_unwrapped_<variant>`). Queued through the mutex
   * so it can't interleave with a mutation's requests, but takes no lock,
   * requests no section, and applies no render — invisible to the user.
   * On failure the sessionStorage guard still covers this tab.
   */
  _persistGiftUnwrap(variantId) {
    if (!variantId) return;
    const attributes = { ["_gift_unwrapped_" + variantId]: "1" };
    this._enqueue(() =>
      postToCart("cart/update.js", { attributes }, this._ac.signal),
    ).catch(() => {});
  }

  _dispatchCartUpdate(cart) {
    const event = new CustomEvent("cart:updated", {
      bubbles: true,
      detail: cart,
    });
    this._ownEvents.add(event);
    document.dispatchEvent(event);
  }
}

customElements.define("cart-drawer", CartDrawer);
